import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Switch, Pagination, Modal, TextField, Grid, Typography } from '@mui/material';
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Dropdown from "react-bootstrap/Dropdown";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { v4 as uuidv4 } from "uuid";
import dayjs from 'dayjs';

import NavBar from "../../structure/NavBar";
import { NavBarTop } from "../../structure/NavBarTop";
import NoDataImg from "../../../assets/img/no-data.gif";
import {
  updateTopHeader,
  updateLoading,
} from "../../../redux/features/common/commonSlice";
import urls from "../../constants/apiurl";
import ApiCaller from "../../constants/ApiCaller";
import { showDangerToast, showToast } from "../../constants/toaster";
import Utils from "../../constants/Utils";
import InsufficientPermission from "../../custom/InSufficientPermission";

const label = { inputProps: { "aria-label": "Size switch demo" } };

// Stable date formatting function outside component
const formatDateStable = (inputDate) => {
  const options = { year: "numeric", month: "short", day: "numeric" };
  const date = new Date(inputDate);
  return date.toLocaleDateString("en-US", options);
};

export const DiscountVouchersList = () => {
  const { formatDate } = Utils();
  const navigate = useNavigate();
  const [callAPI, setCallAPI] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeFilter, setActiveFilter] = useState("");
  const dispatch = useDispatch();
  const [datalist, setDatalist] = useState([]);
  const [sort_type] = useState("createdAt");
  const [sort_order] = useState(false);
  const userData = useSelector((state) => state?.userData);
  const [page, setPage] = useState(1);
  const userPermissions = useSelector((state) => state?.permission);

  const [pagerecord, setPagerecord] = useState({
    totalItems: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    discountPercentage: '',
    price: '',
    maxDiscountAmount: '',
    minOrderAmount: '',
    startDate: null,
    endDate: null,
  });
  const [formErrors, setFormErrors] = useState({});

  // Permission checks
  let currentModulePermission;
  let viewPermission = true;
  let editPermission = true;
  let deletePermission = true;
  let addPermission = true;

  if (userPermissions?.length > 0) {
    currentModulePermission = userPermissions?.find(
      (permission) => permission?.name === "DISCOUNT_VOUCHERS"
    );
    if (currentModulePermission) {
      viewPermission = currentModulePermission?.view;
      editPermission = currentModulePermission?.edit;
      deletePermission = currentModulePermission?.delete;
      addPermission = currentModulePermission?.add;
    }
  }

  useEffect(() => {
    dispatch(updateTopHeader("Discount Vouchers"));
  }, [dispatch]);

  const getDiscountVouchersList = async () => {
    try {
      dispatch(updateLoading(true));

      const params = {
        pageNo: page - 1 || 0,
        pageSize: 10,
        sortBy: sort_type,
        order: sort_order,
        code: searchCode,
        active: activeFilter,
      };

      // Add date filters if present
      if (startDate) {
        params.startDate = formatDateStable(startDate);
      }
      if (endDate) {
        params.endDate = formatDateStable(endDate);
      }

      let path =
        urls.discountVouchersList +
        "?pageNo=" +
        params.pageNo +
        "&pageSize=" +
        params.pageSize +
        "&sortBy=" +
        params.sortBy +
        "&order=" +
        params.order +
        "&code=&active=true";
      console.log(path);
      let res = await ApiCaller.get({}, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200 || response.status === "200") {
        dispatch(updateLoading(false));
        setDatalist(response?.data?.items || []);
        setPagerecord({
          totalItems: response?.data?.totalItems || 0,
          pageNumber: (response?.data?.pageNumber || 0) + 1,
          pageSize: response?.data?.pageSize || 10,
          totalPages: response?.data?.totalPages || 1,
        });
      } else {
        dispatch(updateLoading(false));
        showDangerToast(
          response?.message || "Failed to fetch discount vouchers"
        );
        setDatalist([]);
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error("Error fetching discount vouchers:", error);
      showDangerToast("An unexpected error occurred while fetching data");
      setDatalist([]);
    }
  };

  const handleDeleteVoucher = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this discount voucher?")
    ) {
      try {
        dispatch(updateLoading(true));
        let path = urls.deleteDiscountVoucher + "/" + id;
        let res = await ApiCaller.delete({}, path, userData?.access_token);
        const response = await res.response.json();

        if (response.status === 200 || response.status === "200") {
          dispatch(updateLoading(false));
          showToast(response?.message || "Voucher deleted successfully");
          getDiscountVouchersList();
        } else {
          dispatch(updateLoading(false));
          showDangerToast(response?.message || "Failed to delete voucher");
        }
      } catch (error) {
        dispatch(updateLoading(false));
        console.error("Error deleting voucher:", error);
        showDangerToast("An unexpected error occurred while deleting");
      }
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      dispatch(updateLoading(true));
      let path = urls.toggleDiscountVoucherStatus + "/" + id;
      let res = await ApiCaller.get({}, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200 || response.status === "200") {
        dispatch(updateLoading(false));
        showToast(response?.message || "Status updated successfully");
        getDiscountVouchersList();
      } else {
        dispatch(updateLoading(false));
        showDangerToast(response?.message || "Failed to update status");
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error("Error updating status:", error);
      showDangerToast("An unexpected error occurred while updating status");
    }
  };

  const handleChange = (event, value) => {
    if (typeof value !== "number" || value < 1) return;
    setPage(value);
    setPagerecord((prev) => ({
      ...prev,
      pageNumber: value,
    }));
    setCallAPI(uuidv4());
  };

  const handleFilterClick = useCallback(() => {
    setPage(1);
    setPagerecord((prev) => ({
      ...prev,
      pageNumber: 1,
    }));
    setCallAPI(uuidv4());
  }, []);

  const resetFilter = useCallback(() => {
    setSearchCode("");
    setStartDate(null);
    setEndDate(null);
    setActiveFilter("");
    setPage(1);
    setPagerecord((prev) => ({
      ...prev,
      pageNumber: 1,
    }));
    setCallAPI(uuidv4());
  }, []);

  // Modal functions
  const openAddModal = () => {
    setIsEditMode(false);
    setFormData({
      id: '',
      discountPercentage: '',
      price: '',
      maxDiscountAmount: '',
      minOrderAmount: '',
      startDate: null,
      endDate: null,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (voucherData) => {
    setIsEditMode(true);
    setFormData({
      id: voucherData.id || '',
      discountPercentage: voucherData.discountPercentage || '',
      price: voucherData.price || '',
      maxDiscountAmount: voucherData.maxDiscountAmount || '',
      minOrderAmount: voucherData.minOrderAmount || '',
      startDate: voucherData.startDate ? dayjs(voucherData.startDate) : null,
      endDate: voucherData.endDate ? dayjs(voucherData.endDate) : null,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      id: '',
      discountPercentage: '',
      price: '',
      maxDiscountAmount: '',
      minOrderAmount: '',
      startDate: null,
      endDate: null,
    });
    setFormErrors({});
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.discountPercentage || formData.discountPercentage <= 0) {
      newErrors.discountPercentage = 'Discount percentage is required and must be greater than 0';
    } else if (formData.discountPercentage > 100) {
      newErrors.discountPercentage = 'Discount percentage cannot exceed 100%';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Price is required and must be greater than 0';
    }

    if (!formData.maxDiscountAmount || formData.maxDiscountAmount <= 0) {
      newErrors.maxDiscountAmount = 'Max discount amount is required and must be greater than 0';
    }

    if (!formData.minOrderAmount || formData.minOrderAmount <= 0) {
      newErrors.minOrderAmount = 'Min order amount is required and must be greater than 0';
    }

    // Validate that max discount is reasonable compared to price
    if (formData.maxDiscountAmount && formData.price && 
        parseFloat(formData.maxDiscountAmount) > parseFloat(formData.price)) {
      newErrors.maxDiscountAmount = 'Max discount amount cannot be greater than voucher price';
    }

    // Validate dates if provided
    if (formData.startDate && formData.endDate) {
      if (dayjs(formData.endDate).isBefore(dayjs(formData.startDate))) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Handle date changes
  const handleDateChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showDangerToast('Please fix the validation errors');
      return;
    }

    try {
      dispatch(updateLoading(true));

      // Prepare data for API
      const apiData = {
        discountPercentage: parseFloat(formData.discountPercentage),
        price: parseFloat(formData.price),
        maxDiscountAmount: parseFloat(formData.maxDiscountAmount),
        minOrderAmount: parseFloat(formData.minOrderAmount),
        startDate: formData.startDate ? dayjs(formData.startDate).format('YYYY-MM-DD') + ' 00:00:00' : '',
        endDate: formData.endDate ? dayjs(formData.endDate).format('YYYY-MM-DD') + ' 23:59:59' : '',
      };

      // Add id for update operations
      if (isEditMode && formData.id) {
        apiData.id = formData.id;
      }

      let path = urls.addEditDiscountVoucher;
      let res = await ApiCaller.post(apiData, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200 || response.status === '200') {
        dispatch(updateLoading(false));
        showToast(
          response?.message || 
          `Discount voucher ${isEditMode ? 'updated' : 'created'} successfully`
        );
        closeModal();
        getDiscountVouchersList();
      } else {
        dispatch(updateLoading(false));
        showDangerToast(
          response?.message || 
          `Failed to ${isEditMode ? 'update' : 'create'} discount voucher`
        );
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error('Error saving discount voucher:', error);
      showDangerToast('An unexpected error occurred while saving');
    }
  };

  useEffect(() => {
    if (viewPermission) {
      const fetchData = async () => {
        try {
          dispatch(updateLoading(true));
          const params = {
            pageNo: page - 1 || 0,
            pageSize: 10,
            sortBy: sort_type,
            order: sort_order,
            code: searchCode,
            active: activeFilter,
          };

          // Add date filters if present
          if (startDate) {
            params.startDate = formatDateStable(startDate);
          }
          if (endDate) {
            params.endDate = formatDateStable(endDate);
          }

          let path =
            urls.discountVouchersList +
            "?pageNo=" +
            params.pageNo +
            "&pageSize=" +
            params.pageSize +
            "&sortBy=" +
            params.sortBy +
            "&order=" +
            params.order +
            "&code=&active=true";
          console.log("Path", path);
          let res = await ApiCaller.get({}, path, userData?.access_token);
          console.log("Res", res);
          const response = await res.response.json();

          if (response.status === 200 || response.status === "200") {
            dispatch(updateLoading(false));
            setDatalist(response?.data?.items || []);
            setPagerecord({
              totalItems: response?.data?.totalItems || 0,
              pageNumber: (response?.data?.pageNumber || 0) + 1,
              pageSize: response?.data?.pageSize || 10,
              totalPages: response?.data?.totalPages || 1,
            });
          } else {
            dispatch(updateLoading(false));
            showDangerToast(
              response?.message || "Failed to fetch discount vouchers"
            );
            setDatalist([]);
          }
        } catch (error) {
          dispatch(updateLoading(false));
          console.error("Error fetching discount vouchers:", error);
          showDangerToast("An unexpected error occurred while fetching data");
          setDatalist([]);
        }
      };

      fetchData();
    }
  }, [
    viewPermission,
    callAPI,
    page,
    searchCode,
    activeFilter,
    startDate,
    endDate,
    dispatch,
    userData?.access_token,
    sort_type,
    sort_order,
  ]);

  if (!viewPermission) {
    return <InsufficientPermission />;
  }

  return (
    <>
      <NavBar />
      <div className="main-content" id="panel">
        <NavBarTop />
        <div className="header bg-primary pb-6">
          <div className="container-fluid">
            <div className="header-body">
              <div className="row align-items-center py-4">
                <div className="col-lg-12">
                  <h6 className="h2 text-white d-inline-block mb-0">
                    Discount Vouchers Management
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid mt--6">
          <div className="row">
            <div className="col">
              <div className="card">
                {/* Header with Title and Add Button */}
                <div className="card-header border-0">
                  <div className="row align-items-center">
                    <div className="col">
                      <h3 className="mb-0">Discount Vouchers</h3>
                    </div>
                    <div className="col text-right">
                      {addPermission && (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<AddIcon />}
                          onClick={openAddModal}
                          size="medium"
                        >
                          Create Voucher
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Filters Section */}
                <div className="card-header border-0">
                  <div className="row g-3">
                    {/* First Row - Search and Status */}
                    <div className="col-lg-4 col-md-6">
                      <Form.Label className="form-control-label">Search Voucher</Form.Label>
                      <InputGroup>
                        <Form.Control
                          placeholder="Enter voucher code..."
                          type="search"
                          value={searchCode}
                          onChange={(e) => setSearchCode(e.target.value)}
                        />
                        <InputGroup.Text>
                          <i className="fas fa-search"></i>
                        </InputGroup.Text>
                      </InputGroup>
                    </div>
                    
                    <div className="col-lg-3 col-md-6">
                      <Form.Label className="form-control-label">Status</Form.Label>
                      <Dropdown>
                        <Dropdown.Toggle 
                          variant="outline-secondary" 
                          className="w-100 text-start"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          {activeFilter === ""
                            ? "All Status"
                            : activeFilter === "true"
                            ? "Active"
                            : "Inactive"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="w-100">
                          <Dropdown.Item onClick={() => setActiveFilter("")}>
                            All Status
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setActiveFilter("true")}>
                            Active
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setActiveFilter("false")}>
                            Inactive
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {/* Second Row - Date Filters */}
                    <div className="col-lg-2 col-md-6">
                      <Form.Label className="form-control-label">Start Date</Form.Label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={startDate}
                          onChange={(newValue) => setStartDate(newValue)}
                          slotProps={{ 
                            textField: { 
                              size: "small",
                              fullWidth: true,
                              variant: "outlined"
                            } 
                          }}
                        />
                      </LocalizationProvider>
                    </div>

                    <div className="col-lg-2 col-md-6">
                      <Form.Label className="form-control-label">End Date</Form.Label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={endDate}
                          onChange={(newValue) => setEndDate(newValue)}
                          slotProps={{ 
                            textField: { 
                              size: "small",
                              fullWidth: true,
                              variant: "outlined"
                            } 
                          }}
                        />
                      </LocalizationProvider>
                    </div>

                    {/* Action Buttons */}
                    <div className="col-lg-1 col-md-12 d-flex align-items-end">
                      <div className="d-flex gap-2 w-100">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleFilterClick}
                          size="medium"
                          className="flex-fill"
                        >
                          <i className="fas fa-filter me-1"></i>
                          Filter
                        </Button>
                      </div>
                    </div>
                    
                    <div className="col-lg-12">
                      <div className="d-flex justify-content-end">
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={resetFilter}
                          size="medium"
                        >
                          <i className="fas fa-undo me-1"></i>
                          Reset Filters
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Section */}
                <div className="table-responsive">
                  <table className="table align-items-center table-flush">
                    <thead className="thead-light">
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Voucher Code</th>
                        <th scope="col">Discount %</th>
                        <th scope="col">Price</th>
                        <th scope="col">Max Discount</th>
                        <th scope="col">Min Order</th>
                        <th scope="col">Valid From</th>
                        <th scope="col">Valid Till</th>
                        <th scope="col">Status</th>
                        <th scope="col">Created Date</th>
                        {(editPermission || deletePermission) && (
                          <th scope="col">Action</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="list">
                      {datalist?.length > 0 ? (
                        datalist.map((item, index) => (
                          <tr key={item.id || index}>
                            <td>
                              {(pagerecord.pageNumber - 1) *
                                pagerecord.pageSize +
                                index +
                                1}
                            </td>
                            <td>
                              <span className="badge badge-dot mr-4">
                                <span className="status">
                                  {item.voucherCode || "N/A"}
                                </span>
                              </span>
                            </td>
                            <td>{item.discountPercentage || 0}%</td>
                            <td>GHC {item.price || 0}</td>
                            <td>GHC {item.maxDiscountAmount || 0}</td>
                            <td>GHC {item.minOrderAmount || 0}</td>
                            <td>
                              {item.startDate
                                ? formatDate(item.startDate)
                                : "N/A"}
                            </td>
                            <td>
                              {item.endDate ? formatDate(item.endDate) : "N/A"}
                            </td>
                            <td>
                              <Switch
                                {...label}
                                checked={item.active || false}
                                onChange={() =>
                                  handleStatusToggle(item.id, item.active)
                                }
                                disabled={!editPermission}
                              />
                            </td>
                            <td>{formatDate(item.createdAt)}</td>
                            {(editPermission || deletePermission) && (
                              <td>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  {editPermission && (
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      onClick={() => openEditModal(item)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  )}
                                  {deletePermission && (
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() =>
                                        handleDeleteVoucher(item.id)
                                      }
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Box>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={
                              editPermission || deletePermission ? 11 : 10
                            }
                            className="text-center"
                          >
                            <img
                              src={NoDataImg}
                              alt="No data"
                              style={{ width: "200px", height: "200px" }}
                            />
                            <p>No discount vouchers found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Section */}
                {pagerecord?.totalPages > 1 && (
                  <div className="card-footer py-4">
                    <nav aria-label="...">
                      <Box display="flex" justifyContent="center">
                        <Pagination
                          count={pagerecord?.totalPages}
                          page={page}
                          onChange={handleChange}
                          variant="outlined"
                          color="primary"
                        />
                      </Box>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Voucher Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        aria-labelledby="voucher-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" id="voucher-modal-title">
              {isEditMode ? 'Edit Discount Voucher' : 'Create New Discount Voucher'}
            </Typography>
            <IconButton onClick={closeModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleFormSubmit}>
              <Grid container spacing={3}>
                {/* Discount Percentage */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Discount Percentage (%)"
                    type="number"
                    value={formData.discountPercentage}
                    onChange={(e) => handleInputChange('discountPercentage', e.target.value)}
                    error={!!formErrors.discountPercentage}
                    helperText={formErrors.discountPercentage}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    required
                  />
                </Grid>

                {/* Price */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Voucher Price (GHC)"
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    error={!!formErrors.price}
                    helperText={formErrors.price}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>

                {/* Max Discount Amount */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Discount Amount (GHC)"
                    type="number"
                    value={formData.maxDiscountAmount}
                    onChange={(e) => handleInputChange('maxDiscountAmount', e.target.value)}
                    error={!!formErrors.maxDiscountAmount}
                    helperText={formErrors.maxDiscountAmount}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>

                {/* Min Order Amount */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Min Order Amount (GHC)"
                    type="number"
                    value={formData.minOrderAmount}
                    onChange={(e) => handleInputChange('minOrderAmount', e.target.value)}
                    error={!!formErrors.minOrderAmount}
                    helperText={formErrors.minOrderAmount}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>

                {/* Start Date */}
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Start Date (Optional)"
                      value={formData.startDate}
                      onChange={(newValue) => handleDateChange('startDate', newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formErrors.startDate,
                          helperText: formErrors.startDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* End Date */}
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="End Date (Optional)"
                      value={formData.endDate}
                      onChange={(newValue) => handleDateChange('endDate', newValue)}
                      minDate={formData.startDate || undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!formErrors.endDate,
                          helperText: formErrors.endDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={closeModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      {isEditMode ? 'Update' : 'Create'} Voucher
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default DiscountVouchersList;
